import { useCallback, useEffect, useState } from 'react';
import type { ImgHTMLAttributes, SyntheticEvent } from 'react';

const MAX_TIMED_RETRIES = 6;
const FIRST_RETRY_DELAY = 1000;

type ImageProps = ImgHTMLAttributes<HTMLImageElement>;

// Browsers never retry an image that failed to load, so one requested while
// the network is down (e.g. on the login page the inactivity logout opens when
// the computer wakes from sleep) stays broken until the page is reloaded.
// Remounting the <img> makes the browser request it again.
export const Image = ({ onError, ...props }: ImageProps) => {
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!failed) {
      return;
    }

    const retry = () => {
      setFailed(false);
      setAttempt((current) => current + 1);
    };

    window.addEventListener('online', retry);
    const timer =
      attempt < MAX_TIMED_RETRIES
        ? setTimeout(retry, FIRST_RETRY_DELAY * 2 ** attempt)
        : undefined;

    return () => {
      window.removeEventListener('online', retry);
      clearTimeout(timer);
    };
  }, [failed, attempt]);

  const handleError = useCallback(
    (event: SyntheticEvent<HTMLImageElement>) => {
      setFailed(true);
      onError?.(event);
    },
    [onError],
  );

  return <img key={attempt} {...props} onError={handleError} />;
};
